import React, { ReactElement, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Box, Button, MenuItem } from '@material-ui/core';
import { equals } from 'ramda';

import { Region, Chapter, Licence } from '../../../redux';
import { TextInputField } from '../../atoms';
import { FormValidationErrors, LicenceField } from '../../molecules';

import './region-manager-form.styles.scss';
import { useTranslation } from 'react-i18next';

export interface IRegionManagerFormProps {
	region: Region;
	chapters: Chapter[];
	licences: Licence[];
	onLicenceIdsChanged: (licenceIds: string[]) => void;
	saveRegion: (subRegion: Region) => void;
}

export function RegionManagerForm(
	props: IRegionManagerFormProps
): ReactElement {
	const { region, chapters, licences, onLicenceIdsChanged, saveRegion } =
		props;
	const {
		control,
		formState: { errors, submitCount },
		getValues,
		reset,
		watch,
		handleSubmit
	} = useForm<Region>({
		defaultValues: region
	});

	const { t } = useTranslation('contentManager');

	useEffect(() => {
		if (region && !formSynced()) reset(region);
	}, [region]);

	function formSynced(): boolean {
		return equals(getValues(), region);
	}

	function onSubmit(data: Region): void {
		saveRegion(data);
	}

	useEffect(() => {
		const licenceIds: string[] = getValues('licenceIds');
		if (licenceIds !== region.licenceIds) {
			reset({ ...getValues(), chapterId: ' ' });
		}
		if (licenceIds.length > 0) {
			onLicenceIdsChanged(licenceIds);
		}
	}, [watch('licenceIds')]);

	function hasLicenceSelected(): boolean {
		return getValues('licenceIds').length > 0;
	}

	return (
		<form className="region-manager-form" onSubmit={handleSubmit(onSubmit)}>
			<LicenceField
				name="licenceIds"
				control={control}
				required={t('commonFormfields.requiredLicence')}
				label={t('commonFormfields.licences')}
				className="licence-field"
				licences={licences}
			/>
			<Box>
				<TextInputField
					name="name"
					control={control}
					required={t('regions.formFields.requiredNameRegion')}
					label={t('regions.formFields.regionTitle')}
					disabled={!hasLicenceSelected()}
				/>
				<TextInputField
					name="chapterId"
					control={control}
					label={t('commonFormfields.chapter')}
					minLength={{
						value: 4,
						message: t('commonFormfields.requiredChapter')
					}}
					select
					required={t('commonFormfields.requiredChapter')}
					disabled={!hasLicenceSelected()}
				>
					<MenuItem value=" ">
						{t('commonFormfields.noChapter')}
					</MenuItem>
					{chapters.map((chapter: Chapter) => (
						<MenuItem key={chapter.id} value={chapter.id}>
							{chapter.name}
						</MenuItem>
					))}
				</TextInputField>
				<TextInputField
					name="order"
					control={control}
					required={t('commonFormfields.requiredOrderTableContents')}
					label={t('commonFormfields.orderTableContents')}
					disabled={!hasLicenceSelected()}
					type="number"
				/>
			</Box>

			<Button
				type="submit"
				variant="contained"
				color="primary"
				disabled={!hasLicenceSelected()}
			>
				{t('save')}
			</Button>

			<FormValidationErrors errors={errors} submitCount={submitCount} />
		</form>
	);
}
